import React, { memo, useEffect } from 'react';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import { text, colors, dimensions } from '@annote/core/styles';
import { useDataContext } from 'src/context/DataContext';

const FullContent = styled.div`
  font-size: ${text.size.primary};
  line-height: ${text.height.primary};
  padding: 0 ${dimensions.oneInchRem}rem ${dimensions.oneInchRem * 1.25}rem;
  font-family: ${text.family.reading};
  color: ${colors.text.primary};
  user-select: text;
  cursor: text;
  pointer-events: all !important;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  hyphens: auto;

  picture,
  svg,
  img {
    max-width: 100%;
    height: auto;
  }

  h2 {
    font-size: 24px;
    line-height: 31px;
    font-weight: 600;
    margin-bottom: 24px;
  }

  h3 {
    font-size: 20px;
    line-height: 31px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  h4 {
    font-size: 18px;
    line-height: 31px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  video {
    max-width: 100%;
    height: auto;
  }

  pre {
    white-space: pre-wrap;
    white-space: -moz-pre-wrap;
    white-space: -pre-wrap;
    white-space: -o-pre-wrap;
    word-wrap: break-word;
    overflow-x: auto;
  }

  p {
    margin-bottom: 24px;
  }

  a {
    color: ${colors.text.link};
  }

  .pdf-page {
    border: 1px solid ${colors.lines.link};
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin: 0 auto 20px;
  }
`;

interface SourceReaderProps {
  url: string;
  id: string;
  value: string;
}

const SourceReader: React.FC<SourceReaderProps> = ({ url, id, value }) => {
  const { highlightReaderSources } = useDataContext();

  const sanitizedContent = (): { __html: string } => ({
    __html: DOMPurify.sanitize(value),
  });

  useEffect(() => {
    if (highlightReaderSources) {
      // Debounced function to highlight the sources with annote-embed (if no extension)
      highlightReaderSources(url);
    } else {
      // ping the extension to highlight the source
      const event = new CustomEvent('NewSourceToRead', { detail: url });
      window.dispatchEvent(event);
    }
  }, [url, highlightReaderSources]);

  return (
    <FullContent className="nodeselector nodrag" data-reader-source-url={url} data-reader-source-id={id}>
      <div dangerouslySetInnerHTML={sanitizedContent()} />
    </FullContent>
  );
};

export default memo(SourceReader);
